import { queryOptions } from "@tanstack/react-query";
import { workflowService } from "./workflow.service";
import type { WorkflowRulesParams } from "./workflow.types";

export const workflowKeys = {
	all: ["workflows"] as const,
	conditionOperatorsByDataType: () =>
		[...workflowKeys.all, "conditionOperatorsByDataType"] as const,
	rules: () => [...workflowKeys.all, "rules"] as const,
	rulesCount: (params?: WorkflowRulesParams, organizationId?: string) =>
		[
			...workflowKeys.rules(),
			"count",
			{ params, organizationId: organizationId ?? null },
		] as const,
	rulesList: (
		start: number,
		count: number,
		params?: WorkflowRulesParams,
		organizationId?: string,
	) =>
		[
			...workflowKeys.rules(),
			"list",
			start,
			count,
			{ params, organizationId: organizationId ?? null },
		] as const,
	ruleDetail: (ruleId: string) =>
		[...workflowKeys.rules(), "detail", ruleId] as const,
};

export const workflowQueryOptions = {
	/**
	 * Static lookup: maps each data type to its allowed condition operators.
	 * Rarely changes — cache for 30 minutes.
	 */
	conditionOperatorsByDataType: queryOptions({
		queryKey: workflowKeys.conditionOperatorsByDataType(),
		queryFn: () => workflowService.getConditionOperatorsByDataType(),
		staleTime: 30 * 60 * 1000,
		gcTime: 60 * 60 * 1000,
	}),
	rules: {
		/**
		 * Paginated list of workflow rules.
		 * `start` is 1-based (pageIndex * pageSize + 1).
		 */
		list: (
			start: number,
			count: number,
			params?: WorkflowRulesParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: workflowKeys.rulesList(
					start,
					count,
					params,
					options?.organizationId,
				),
				queryFn: () =>
					workflowService.getWorkflowRules(start, count, params, options),
				staleTime: 0,
				gcTime: 5 * 60 * 1000,
			}),

		/**
		 * Total count of workflow rules matching params.
		 */
		count: (
			params?: WorkflowRulesParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: workflowKeys.rulesCount(params, options?.organizationId),
				queryFn: () => workflowService.getWorkflowRulesCount(params, options),
				staleTime: 0,
				gcTime: 5 * 60 * 1000,
			}),

		/**
		 * Single workflow rule by id.
		 */
		detail: (ruleId: string, options?: { organizationId?: string }) =>
			queryOptions({
				queryKey: workflowKeys.ruleDetail(ruleId),
				queryFn: () => workflowService.getWorkflowRule(ruleId, options),
				staleTime: 0,
				gcTime: 5 * 60 * 1000,
				enabled: !!ruleId,
			}),
	},
};
